/**
 * The sound catalog: the fixed set of bundled click sounds the user can
 * choose from. Each entry has a stable id (used for persistence, so it must
 * never change), the wav file it loads from, and a friendly display name.
 */
const CLICK_SOUNDS = [
    {id: 'click', name: 'Click', file: 'assets/click.wav'},
    {id: 'sine_1', name: 'Sine 1', file: 'assets/sine_1.wav'},
    {id: 'sine_2', name: 'Sine 2', file: 'assets/sine_2.wav'},
    {id: 'wood_1', name: 'Wood 1', file: 'assets/wood_1.wav'},
    {id: 'wood_2', name: 'Wood 2', file: 'assets/wood_2.wav'},
    {id: 'noise_click', name: 'Noise Click', file: 'assets/noise_click.wav'},
] as const;

type ClickSoundId = typeof CLICK_SOUNDS[number]['id'];

/** The two click-sound slots the user can assign sounds to. */
type SoundSection = 'downbeat' | 'secondary';

const DEFAULT_DOWNBEAT_SOUND: ClickSoundId = 'wood_2';
const DEFAULT_SECONDARY_SOUND: ClickSoundId = 'click';

function isClickSoundId(value: unknown): value is ClickSoundId {
    return typeof value === 'string'
        && (CLICK_SOUNDS as readonly { id: string }[]).some(sound => sound.id === value);
}

/**
 * Coerce a persisted value to a valid sound id, falling back to the default
 * when it is missing or no longer in the catalog.
 */
function toClickSoundId(value: string | null, fallback: ClickSoundId): ClickSoundId {
    return isClickSoundId(value) ? value : fallback;
}

function clickSoundName(id: ClickSoundId): string {
    return CLICK_SOUNDS.find(sound => sound.id === id)?.name ?? id;
}

export {
    CLICK_SOUNDS,
    ClickSoundId,
    SoundSection,
    DEFAULT_DOWNBEAT_SOUND,
    DEFAULT_SECONDARY_SOUND,
    isClickSoundId,
    toClickSoundId,
    clickSoundName,
};
